import { RefObject } from 'react';

export interface StreamCursor<C> {
  step: number;
  data: C;
}

type DataCallback<T, C> = (data: T, cursor: StreamCursor<C>) => void;
type VoidFN = () => void;

export interface Stream<T, C> {
  onData: (cb: DataCallback<T, C>) => Stream<T, C>;
  onComplete: (cb: VoidFN) => Stream<T, C>;
  cancel: VoidFN;
  start: VoidFN;
  loop: (cb: StreamCursor<C>) => void;
  close: VoidFN;
}

export const getCursorStream = <T, C>(
  getCursor: (step: number) => StreamCursor<C> | null,
  step: (cursor: StreamCursor<C>) => Promise<T>,
  mountedRef: RefObject<boolean>,
) => {
  const subscribers: DataCallback<T, C>[] = [];
  const completeSubscribers: VoidFN[] = [];
  let index = 0;

  const stream: Stream<T, C> = {
    onData: (cb: DataCallback<T, C>) => {
      subscribers.push(cb);
      return stream;
    },
    onComplete: (cb: VoidFN) => {
      completeSubscribers.push(cb);
      return stream;
    },
    cancel: () => {
      subscribers.length = 0;
      completeSubscribers.length = 0;
    },
    start: () => {
      index = 0;
      const cursor = getCursor(index);
      if (!cursor) return stream.close();
      stream.loop(cursor);
    },
    loop: async (cursor: StreamCursor<C>) => {
      const result = await step(cursor);
      subscribers.forEach(cb => cb(result, cursor));
      index++;

      if (mountedRef && subscribers.length) {
        const cursor = getCursor(index);
        if (!cursor) return stream.close();
        stream.loop(cursor);
      }
    },
    close: () => {
      completeSubscribers.forEach(cb => cb());
      subscribers.length = 0;
      completeSubscribers.length = 0;
    },
  };

  return stream;
};
